import {
    Component,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    ViewChild,
    HostListener,
    Inject,
    OnDestroy,
    forwardRef,
    OnInit
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NG_VALIDATORS,
    Validator,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import {
    autocompleteChipStylesSAG,
    autocompleteChipStylesSHS,
    autocompleteStylesSAG,
    autocompleteStylesSHS,
    inputStylesSAG,
    inputStylesSHS
} from './multi-autocomplete.styles';
import { componentStylesByTheme } from '../utils/theme.utils';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize, tap } from 'rxjs/operators';
import { SearchFn, SearchFnObservable } from '../../public-api';

@Component({
    selector: 'sag-ui-multi-autocomplete',
    templateUrl: './multi-autocomplete.component.html',
    styleUrls: [],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiAutocompleteComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => MultiAutocompleteComponent),
            multi: true
        }
    ]
})
export class MultiAutocompleteComponent<T> implements ControlValueAccessor, Validator, OnDestroy, OnInit {
    @Input() id = this.randomId();
    @Input() placeholder = '';
    @Input() required = false;
    @Input() disabled = false;
    @Input() readOnly = false;
    @Input() label = '';
    @Input() value: T[] | null = null;
    @Input() invalid = false;
    @Input() error: string | string[] = '';
    @Input() hint?: string;
    @Input() ariaLabel: string | null = null;
    @Input() ariaLabelledby: string | null = null;
    @Input() ariaDescribedby: string | null = null;
    @Input() options: { label: string; value: T }[] = [];
    @Input()
    searchFn: SearchFn = async () => void 0;
    @Input()
    searchFn$: SearchFnObservable;
    @Input()
    compareWith: (o1: T, o2: T) => boolean = (o1, o2) => {
        if (typeof o1 === 'object' && typeof o2 === 'object') {
            return JSON.stringify(o1) === JSON.stringify(o2);
        }
        return o1 === o2;
    };

    @Output() change: EventEmitter<T[]> = new EventEmitter<T[]>();
    @Output() itemCreated: EventEmitter<string> = new EventEmitter<string>();
    @Output() click = new EventEmitter<Event>();
    @Output() focus = new EventEmitter<Event>();
    @Output() blur = new EventEmitter<Event>();
    @Output() keydown = new EventEmitter<KeyboardEvent>();
    @Output() keyup = new EventEmitter<KeyboardEvent>();
    @Output() mouseenter = new EventEmitter<MouseEvent>();
    @Output() mouseleave = new EventEmitter<MouseEvent>();

    @ViewChild('input') input!: ElementRef;

    @HostListener('document:mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen = false;
        }
    }

    @HostListener('focusout', ['$event'])
    onFocusOut(event: FocusEvent): void {
        const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
        if (!focusedInside) {
            this.isOpen = false;
        }
    }

    protected get popoverClasses(): string {
        const popoverStyles = componentStylesByTheme(this.theme, autocompleteStylesSAG, autocompleteStylesSHS);
        return popoverStyles();
    }

    protected get inputClasses(): string {
        const inputStyles = componentStylesByTheme(this.theme, inputStylesSAG, inputStylesSHS);
        return inputStyles();
    }

    protected get chipClasses(): string {
        const chipStyles = componentStylesByTheme(this.theme, autocompleteChipStylesSAG, autocompleteChipStylesSHS);
        return chipStyles();
    }

    protected get inputValue(): string {
        return this.input.nativeElement.value;
    }

    protected touched = false;
    protected selectedItems: { label: string; value: any }[] = [];
    protected isOpen = false;
    protected filterValue = '';
    protected searchSubject: Subject<string> = new Subject<string>();
    protected currentPage = 0;
    protected loading = false;

    private subscriptions: Subscription[] = [];

    constructor(@Inject(THEME_TOKEN) private readonly theme: Theme, private el: ElementRef) {}

    ngOnInit(): void {
        this.initSearchSubject();
        this.initSelectedItems();
        this.loadOptions('', 1);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    protected onFocus() {
        if (this.disabled || this.readOnly) return;
        this.isOpen = true;
    }

    protected onBlur() {
        this.markAsTouched();
    }

    protected togglePopover(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        if (this.isOpen) {
            this.isOpen = false;
            this.input.nativeElement.blur();
        } else {
            this.isOpen = true;
            this.input.nativeElement.focus();
        }
    }

    protected handleInput(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.filterValue = target.value;
        this.searchSubject.next(this.filterValue);
    }

    protected get displayValue(): string {
        return this.selectedItems.map(item => item.label).join(', ');
    }

    protected handleOptionSelect(data: [string, any]): void {
        const [label, value] = data;
        this.input.nativeElement.focus();
        this.markAsTouched();
        if (this.selectedItems.some(item => this.compareWith(item.value, value))) {
            this.selectedItems = this.selectedItems.filter(item => !this.compareWith(item.value, value));
            this.writeValue(this.selectedItems.map(item => item.value));
        } else {
            this.selectedItems.push({ label, value });
            this.writeValue(this.selectedItems.map(item => item.value));
        }

        this.input.nativeElement.value = '';
        this.filterValue = '';
        this.searchSubject.next(this.filterValue);
        this.onFocus();
    }

    protected onScroll(event: Event): void {
        const target = event.target as HTMLElement;
        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 1) {
            this.currentPage++;
            this.loadOptions(this.filterValue, this.currentPage);
        }
    }

    protected isArray(value: string | string[]): value is string[] {
        return Array.isArray(value);
    }

    // ControlValueAccessor implementation
    onChange = (_value: any) => {};
    onTouch = () => {};

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    public writeValue(val: T[]): void {
        this.value = val;
        this.onChange(this.value);
        this.change.emit(this.value);
    }

    public markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }

    public setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    public validate(_: AbstractControl<any, any>): ValidationErrors | null {
        return this.required && !this.selectedItems.length ? { required: true } : null;
    }

    public removeItem(index: number): void {
        this.selectedItems.splice(index, 1);
        this.writeValue(this.selectedItems.map(item => item.value));
    }

    public handleAddOption(): void {
        const value = this.input.nativeElement.value;
        this.handleOptionSelect([value, value]);
        this.itemCreated.emit(value);
    }

    public keydownHandler(event: KeyboardEvent): void {
        if (event.code === 'Enter') {
            event.preventDefault();
            this.handleAddOption();
        }

        this.keydown.emit(event);
    }

    private randomId(): string {
        return Math.random().toString(36).substring(2);
    }

    private initSearchSubject() {
        this.subscriptions.push(
            this.searchSubject
                .pipe(
                    debounceTime(500),
                    tap(searchText => {
                        this.currentPage = 0;
                        this.loadOptions(searchText, this.currentPage);
                    })
                )
                .subscribe()
        );
    }

    private initSelectedItems() {
        if (this.value) {
            this.selectedItems = this.options.filter(option =>
                this.value?.some(value => this.compareWith(value, option.value))
            );
        }
    }

    private async loadOptions(search: string, page: number): Promise<void> {
        this.loading = true;

        if (this.searchFn) {
            try {
                await this.searchFn(search, page);
            } finally {
                this.loading = false;
            }
        }

        if (this.searchFn$) {
            this.searchFn$(search, page).pipe(finalize(() => (this.loading = false)));
        }
    }
}
